import React, { useEffect, useMemo, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, HStack, Input, Switch, Button, useToast, VStack, Text } from "@chakra-ui/react";
import client from "../../../helpers/Client";

export default function AdminIssueKinds() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState([]);
  const [aliases, setAliases] = useState([]);

  // Fetch choices from OPTIONS
  useEffect(() => {
    client.request({ url: '/public/tickets/', method: 'OPTIONS' })
      .then(({ data }) => {
        const kinds = data?.actions?.POST?.kind?.choices || [];
        setChoices(kinds);
      })
      .catch(() => toast({ title: 'Seçenekler alınamadı', status: 'error' }));
  }, [toast]);

  // Fetch existing aliases
  useEffect(() => {
    client.get('/config/kinds/')
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
        setAliases(items);
      })
      .catch(() => setAliases([]));
  }, []);

  const aliasMap = useMemo(() => {
    const m = new Map();
    (Array.isArray(aliases) ? aliases : []).forEach(a => m.set(a.code, a));
    return m;
  }, [aliases]);

  const [form, setForm] = useState({});
  const [newItem, setNewItem] = useState({ code: '', display_name: '', is_active: true });

  useEffect(() => {
    const initial = {};
    choices.forEach(c => {
      const alias = aliasMap.get(c.value);
      initial[c.value] = {
        code: c.value,
        display_name: alias?.display_name || c.display_name,
        is_active: alias?.is_active !== undefined ? alias.is_active : true,
        id: alias?.id,
      };
    });
    setForm(initial);
  }, [choices, aliasMap]);

  const handleChange = (code, key, value) => {
    setForm(prev => ({ ...prev, [code]: { ...prev[code], [key]: value } }));
  };

  const handleSave = async (code) => {
    const entry = form[code];
    if (!entry) return;
    setLoading(true);
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    try {
      const payload = { code: entry.code, display_name: entry.display_name, is_active: entry.is_active };
      let res;
      if (entry.id) {
        await client.patch(`/config/kinds/${entry.id}/`, payload);
      } else {
        await client.post(`/config/kinds/`, payload);
      }
      toast({ title: 'Kaydedildi', status: 'success' });
    } catch (e) {
      toast({ title: 'Kaydetme başarısız', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = async () => {
    if (!newItem.code || !newItem.display_name) {
      toast({ title: 'Kod ve görünen ad gerekli', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await client.post('/config/kinds/', newItem);
      toast({ title: 'Yeni tip eklendi', status: 'success' });
      // Refresh aliases
      const { data } = await client.get('/config/kinds/');
      const items = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      setAliases(items);
      setNewItem({ code: '', display_name: '', is_active: true });
    } catch (e) {
      toast({ title: 'Ekleme başarısız', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>İş/Arıza Tipleri</Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>Mevcut tiplerin görünür adlarını düzenleyin ve görünürlüğünü yönetin.</Text>
      <Box mb={6} p={4} borderWidth="1px" borderRadius="md">
        <Heading size="sm" mb={3}>Yeni Tip Ekle</Heading>
        <HStack spacing={3}>
          <Input placeholder="Kod" value={newItem.code} onChange={(e) => setNewItem({ ...newItem, code: e.target.value })} maxW="220px" />
          <Input placeholder="Görünen Ad" value={newItem.display_name} onChange={(e) => setNewItem({ ...newItem, display_name: e.target.value })} />
          <HStack>
            <Text fontSize="sm">Aktif</Text>
            <Switch isChecked={newItem.is_active} onChange={(e) => setNewItem({ ...newItem, is_active: e.target.checked })} />
          </HStack>
          <Button colorScheme="green" onClick={handleCreateNew} isLoading={loading}>Ekle</Button>
        </HStack>
        <Text fontSize="xs" color="gray.500" mt={2}>Kod yalnızca harf/rakam ve alt çizgi içermeli, benzersiz olmalı.</Text>
      </Box>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Kod</Th>
            <Th>Görünen Ad</Th>
            <Th>Aktif</Th>
            <Th>Aksiyon</Th>
          </Tr>
        </Thead>
        <Tbody>
          {choices.map(c => (
            <Tr key={c.value}>
              <Td>{c.value}</Td>
              <Td>
                <Input value={form[c.value]?.display_name || ''} onChange={(e) => handleChange(c.value, 'display_name', e.target.value)} />
              </Td>
              <Td>
                <Switch isChecked={form[c.value]?.is_active ?? true} onChange={(e) => handleChange(c.value, 'is_active', e.target.checked)} />
              </Td>
              <Td>
                <Button size="sm" colorScheme="blue" isLoading={loading} onClick={() => handleSave(c.value)}>Kaydet</Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <VStack align="start" mt={4} spacing={1}>
        <Text fontSize="xs" color="gray.500">Not: Public ticket formundaki tür seçenekleri alias kayıtlarına göre oluşturulur.</Text>
      </VStack>
    </Box>
  );
}